import { Check, ChevronDown } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { levenshteinDistance } from "src/common/utils";
import { Input } from "src/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";

interface GithubOrg {
  id: string;
  name: string;
}

interface GithubOrgTypeaheadProps {
  orgs: GithubOrg[];
  value: string;
  onChange: (orgId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

interface OrgResultsListProps {
  orgs: GithubOrg[];
  selectedOrgId: string;
  onSelect: (org: GithubOrg) => void;
}

const OrgResultsList = React.memo<OrgResultsListProps>(function OrgResultsList({
  orgs,
  selectedOrgId,
  onSelect,
}) {
  if (orgs.length === 0) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        No organizations found matching your search.
      </div>
    );
  }

  return (
    <div className="p-0.5">
      {orgs.map((org) => (
        <button
          key={org.id}
          onClick={() => onSelect(org)}
          className={`
            w-full flex items-center justify-between px-2 py-1.5 text-xs rounded
            hover:bg-blue-950/40 transition-colors text-left
            ${selectedOrgId === org.id ? "bg-card" : ""}
          `}
        >
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="truncate text-foreground">{org.name}</span>
          </div>
          {selectedOrgId === org.id && (
            <Check className="h-3 w-3 text-primary shrink-0" />
          )}
        </button>
      ))}
    </div>
  );
});

export function GithubOrgTypeahead({
  orgs,
  value,
  onChange,
  disabled = false,
  placeholder = "Select an organization...",
}: GithubOrgTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOrgs = useMemo(() => {
    if (!searchQuery) {
      // If no search query, show selected org first if it exists
      if (value) {
        const selected = orgs.find((o) => o.id === value);
        if (selected) {
          const others = orgs.filter((o) => o.id !== value);
          return [selected, ...others.sort((a, b) => a.name.localeCompare(b.name))];
        }
      }
      return orgs.slice().sort((a, b) => a.name.localeCompare(b.name));
    }

    const query = searchQuery.toLowerCase();

    // Filter orgs that contain the query
    const matchingOrgs = orgs.filter((o) =>
      o.name.toLowerCase().includes(query)
    );

    // Sort by edit distance
    return matchingOrgs.sort((a, b) => {
      const distanceA = levenshteinDistance(query, a.name.toLowerCase());
      const distanceB = levenshteinDistance(query, b.name.toLowerCase());

      if (distanceA === distanceB) {
        return a.name.localeCompare(b.name);
      }

      return distanceA - distanceB;
    });
  }, [orgs, searchQuery, value]);

  // Show top 10 results
  const limitedOrgs = useMemo(() => filteredOrgs.slice(0, 10), [filteredOrgs]);

  const selectedOrg = orgs.find((o) => o.id === value);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  const handleSelect = (org: GithubOrg) => {
    onChange(org.id);
    setOpen(false);
    setSearchQuery("");
    setIsTyping(false);
  };

  const handleButtonClick = () => {
    if (disabled) return;
    if (!isTyping) {
      setIsTyping(true);
      setOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  // Handle popover close
  useEffect(() => {
    if (!open) {
      setIsTyping(false);
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          onClick={handleButtonClick}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-border bg-transparent px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isTyping ? (
            <>
              <Input
                ref={inputRef}
                placeholder="Search organizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-0 h-auto p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          ) : (
            <>
              <span className="truncate">
                {selectedOrg ? (
                  <span className="text-foreground">{selectedOrg.name}</span>
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover border-border"
        side="bottom"
        align="start"
        sideOffset={0}
        style={{
          width: triggerWidth > 0 ? `${triggerWidth}px` : "100%",
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <div
          className="max-h-[300px] overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
          onWheel={(e) => {
            const container = e.currentTarget;
            const delta = e.deltaY;
            container.scrollTop += delta;
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <OrgResultsList
            orgs={limitedOrgs}
            selectedOrgId={value}
            onSelect={handleSelect}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
